import React, { useState } from 'react';
import {
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  Alert,
  Grid,
} from '@mui/material';
import { useAuth } from '@healthdesk/shared-hooks';
import useClinicAnalytics from './useClinicAnalytics';
import TodaySnapshot from './TodaySnapshot';
import VolumeTrendChart from './VolumeTrendChart';
import WaitTimeChart from './WaitTimeChart';
import PeakHoursChart from './PeakHoursChart';
import PatientMixChart from './PatientMixChart';
import PremiumWrapper from '../../../../components/dashboard/PremiumWrapper';
import PremiumAlert from '../../../../components/dashboard/PremiumAlert';

export default function ClinicHealthDashboard({ previewMode = false }) {
  const { userData } = useAuth();
  const facilityId = userData?.facilityId;

  const [rangeDays, setRangeDays] = useState(30);

  const { todayStats, trendData, loading, error } = useClinicAnalytics(
    facilityId,
    rangeDays,
  );

  const handleRangeChange = (_e, val) => {
    if (val !== null) setRangeDays(val);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>;
  }

  return (
    <Box>
      {/* ---- Free tier: today's numbers ---- */}
      <TodaySnapshot stats={todayStats} />

      {/* ---- Premium: historical trends ---- */}
      {previewMode && (
        <PremiumAlert feature="Clinic analytics trends" />
      )}

      <PremiumWrapper disabled={previewMode}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6">Trends</Typography>

          <ToggleButtonGroup
            value={rangeDays}
            exclusive
            onChange={handleRangeChange}
            size="small"
          >
            <ToggleButton value={7}>7 d</ToggleButton>
            <ToggleButton value={30}>30 d</ToggleButton>
            <ToggleButton value={90}>90 d</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <VolumeTrendChart data={trendData?.dailyVolume} />
          </Grid>
          <Grid item xs={12} md={6}>
            <WaitTimeChart data={trendData?.dailyWaitTime} />
          </Grid>
          <Grid item xs={12} md={6}>
            <PeakHoursChart data={trendData?.peakHours} />
          </Grid>
          <Grid item xs={12} md={6}>
            <PatientMixChart data={trendData?.patientMix} />
          </Grid>
        </Grid>
      </PremiumWrapper>
    </Box>
  );
}
